const Viewport = require('./Viewport');

const BASE_MARGIN = 5,
    CONNECTOR_MARGIN = 4,
    VERTICAL_POSITIONS = ['top', 'bottom'],
    HORIZONTAL_POSITIONS = ['left', 'right'],
    VERTICAL_ALIGNMENTS = ['top', 'middle', 'bottom'],
    HORIZONTAL_ALIGNMENTS = ['left', 'center', 'right'];

/**
 * Makes calculations for box-shadow sizes
 * @param  {HTMLElmenet} element Element source for box-shadow sizes
 * @return {object} object.top {Number}
 *                  object.bottom {Number}
 *                  object.left {Number}
 *                  object.right {Number}
 */
function getBoxShadowSize(element) {
    var style = window.getComputedStyle(element),
        shadowSize,
        shadowSizeArray,
        hShadow,
        vShadow,
        spread,
        blur,
        result = {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0
        };

    shadowSize = style.getPropertyValue('box-shadow');
    if (shadowSize === 'none') {
        return result;
    }

    // Remove all possible color definitions
    shadowSize = shadowSize.replace(/rgba?\([^\)]+\)/gi, '');
    shadowSize = shadowSize.replace(/#[0-9a-f]+/gi, '');

    // Remove any alpha characters
    shadowSize = shadowSize.replace(/[a-z]+/gi, '').trim();

    shadowSizeArray = shadowSize.split(' ');

    hShadow = +shadowSizeArray[0];
    vShadow = +shadowSizeArray[1];
    blur = +shadowSizeArray[2];
    spread = +shadowSizeArray[3];

    result.left = Math.max(spread - hShadow + 0.5 * blur, 0);
    result.right = Math.max(spread + hShadow + 0.5 * blur, 0);
    result.top = Math.max(spread - vShadow + 0.5 * blur, 0);
    result.bottom = Math.max(spread + vShadow + 0.5 * blur, 0);

    return result;

}

/**
 * Some math to retrieve the current position of the target element.
 * @argument {HTMLElement} The element to get the position.
 *
 * @return {object} object.x {Number}
 *                  object.y {Number}
 */
function _getAbsolutePosition(elem) {
    var x = 0,
        y = 0;
    // offset is relative to parent so,
    // we traverse all the tree up until
    // target has no more parents.
    do {
        if (elem) {
            x += parseInt(elem.offsetTop, 10);
            y += parseInt(elem.offsetLeft, 10);
            elem = elem.offsetParent;
        }
    } while (elem && !_generatesNewStackingContext(elem));
    return {
        x: x,
        y: y
    };
}

/**
 * Determines if the element is in a stacked context.
 * @param {HTMLElement} elem Element source to check stacked context
 * @returns {boolean}
 * @private
 */
function _generatesNewStackingContext(elem) {
    var styles = elem ? window.getComputedStyle(elem) : null,
        pos;
    if (styles) {
        pos = styles.position;
        if (pos === 'absolute' || pos === 'relative' || pos === 'fixed' ) {
            return true;
        }
    }
    return false;
}

/**
 * Determines if the element has non-zero offsetWidth and offsetHeight.
 * @param  {HTMLElement} elem Element to detect width and height
 * @return {Boolean}      True if the element has non-zero width and height
 */
function _hasDimension(elem) {
    return (elem.offsetWidth > 0) && (elem.offsetHeight > 0);
}

/**
 * Retrieves the dimensions of the object.
 *
 * @argument {HTMLElement} The element which we want to get the dimension.
 *
 * @return {object} object.width {Number}
 *                  object.height {Number}
 */
function _getDimension(elem) {
    var dimensions = {},
        style = {
            'position': '',
            'display': '',
            'top': '',
            'left': ''
        },
        moved = false,
        domRect,
        rect = {},
        key = '';

    // If we have dimensions we can just use it.
    if (!_hasDimension(elem)) {
        domRect = elem.getBoundingClientRect();

        if (!domRect.width || !domRect.height) {

            // If we don't have dimensions we need to put the elem in a state
            // that we can truly get the width and height and then restore
            // everything.
            moved = true;
            for (key in style) {
                style[key] = elem.style[key] || '';
            }

            // @TODO Maybe a better strategy here. The main issue with this
            // is performance.
            elem.style.position = 'absolute';
            elem.style.left = '-1000px';
            elem.style.top = '-1000px';
            elem.style.display = 'inline-block';
        } else {
            rect.width = domRect.width;
            rect.height = domRect.height;
        }
    }
    dimensions = {
        width: rect.width || elem.offsetWidth,
        height: rect.height || elem.offsetHeight
    };

    if (moved) {
        for (key in style) {
            elem.style[key] = style[key];
        }
    }

    return dimensions;
}

/**
 * Retrieve the position and the dimensions of an element.
 *
 * @return {object} object.left {Number}
 *                  object.top {Number}
 *                  object.right {Number}
 *                  object.bottom {Number}
 *                  object.width {Number}
 *                  object.height {Number}
 */
function _getRect(elem) {
    var rect = {},
        position = _getAbsolutePosition(elem),
        dimension = _getDimension(elem);
    rect.left = position.y;
    rect.right = position.y + dimension.width;
    rect.top = position.x;
    rect.bottom = position.x + dimension.height;
    rect.width = dimension.width;
    rect.height = dimension.height;

    return rect;
}

/**
 * Math to determine if the top position is available.
 * @argument {object} targetRect            position and dimensions of the parent object.
 * @argument {object} childRect             position and dimensions of the child object.
 * @argument {Number} margin                margin between the popup and the target
 * @argument {Object} boxShadow             calculated size of box-shadow property of child
 * @argument {object} currentPosition       current calculations for positioning the popup
 * @argument {object} currentPositionType   current type of position or alignment
 *
 * @return {object} result
 */
function _tryTopPosition(targetRect, childRect, margin, boxShadow, currentPosition, currentPositionType) {
    var available = targetRect.top,
        finalMargin = margin + boxShadow.bottom,
        needed = childRect.height + finalMargin;

    if (needed < available) {
        currentPositionType.position = 'top';
        currentPosition.x = targetRect.top - needed;
    }
}

/**
 * Math to determine if the bottom position is available.
 *
 * @argument {object}   targetRect          position and dimensions of the parent object.
 * @argument {object}   childRect           position and dimensions of the child object.
 * @argument {Number}   margin              margin between the popup and the target
 * @argument {Object}   boxShadow           calculated size of box-shadow property of child
 * @argument {object}   currentPosition     current calculations for positioning the popup
 * @argument {object}   currentPositionType current type of position or alignment
 * @argument {Boolean}  force               when set on true, forces the positioning (used as fallback)
 *
 * @return {object} result
 */
function _tryBottomPosition(targetRect, childRect, margin, boxShadow, currentPosition, currentPositionType, force) {
    var finalMargin = margin + boxShadow.top,
        available = _getPageSize(this.ownerDocument).height - targetRect.bottom,
        needed = childRect.height + finalMargin;

    if ((needed < available) || force) {
        currentPositionType.position = 'bottom';
        currentPosition.x = targetRect.bottom + finalMargin;
    }
}

/**
 * Math to determine if the right position is available.
 *
 * @argument {object} targetRect            position and dimensions of the parent object.
 * @argument {object} childRect             position and dimensions of the child object.
 * @argument {Number} margin                margin between the popup and the target
 * @argument {Object} boxShadow             calculated size of box-shadow property of child
 * @argument {object} currentPosition       current calculations for positioning the popup
 * @argument {object} currentPositionType   current type of position or alignment
 *
 * @return {object} result
 */
function _tryRightPosition(targetRect, childRect, margin, boxShadow, currentPosition, currentPositionType) {
    var finalMargin = margin + boxShadow.left,
        available = _getPageSize(this.ownerDocument).width - targetRect.right,
        needed = childRect.width + finalMargin;

    if (needed < available) {
        currentPositionType.position = 'right';
        currentPosition.y = targetRect.right + finalMargin;
    }
}

/**
 * Math to determine if the left position is available.
 *
 * @argument {object} targetRect            position and dimensions of the parent object.
 * @argument {object} childRect             position and dimensions of the child object.
 * @argument {Number} margin                margin between the popup and the target
 * @argument {Object} boxShadow             calculated size of box-shadow property of child
 * @argument {object} currentPosition       current calculations for positioning the popup
 * @argument {object} currentPositionType   current type of position or alignment
 *
 * @return {object} result
 */
function _tryLeftPosition(targetRect, childRect, margin, boxShadow, currentPosition, currentPositionType) {
    var finalMargin = margin + boxShadow.right,
        available = targetRect.left,
        needed = childRect.width + finalMargin;

    if (needed < available) {
        currentPositionType.position = 'left';
        currentPosition.y = targetRect.left - needed;
    }
}

/**
 * Math to determine if the left alignment is available.
 *
 * @argument {object} targetRect            position and dimensions of the parent object.
 * @argument {object} childRect             position and dimensions of the child object.
 * @argument {object} currentPosition       current calculations for positioning the popup
 * @argument {object} currentPositionType   current type of position or alignment
 *
 * @return {object} result
 */
function _tryLeftAlignment(targetRect, childRect, currentPosition, currentPositionType) {
    var available = _getPageSize(this.ownerDocument).width - targetRect.left,
        needed = childRect.width;

    if (needed < available) {
        currentPositionType.alignment = 'left';
        currentPosition.y = targetRect.left;
    }
}

/**
 * Math to determine if the center alignment is available.
 *
 * @argument {object} targetRect            position and dimensions of the parent object.
 * @argument {object} childRect             position and dimensions of the child object.
 * @argument {object} currentPosition       current calculations for positioning the popup
 * @argument {object} currentPositionType   current type of position or alignment
 *
 * @return {object} result
 */
function _tryCenterAlignment(targetRect, childRect, currentPosition, currentPositionType) {
    var available = _getPageSize(this.ownerDocument).width,
        needed = childRect.width,
        halfChild,
        targetCenterPos;

    if (needed < available) {
        currentPositionType.alignment = 'center';

        halfChild = childRect.width / 2;
        targetCenterPos = targetRect.left + (targetRect.width / 2);

        if (targetCenterPos > halfChild) {
            // Popup left half fits
            if (targetCenterPos + halfChild < available) {
                // Popup fits centered
                currentPosition.y = targetCenterPos - halfChild;
            } else {
                // Popup doesn't fit on the right, so will be sticky to that side
                currentPosition.y = available - childRect.width;
            }
        } else {
            // Popup doesn't fit on the left, so will be sticky to that side
            currentPosition.y = 0;
        }
    }
}

/**
 * Math to determine if the right alignment is available.
 *
 * @argument {object} targetRect            position and dimensions of the parent object.
 * @argument {object} childRect             position and dimensions of the child object.
 * @argument {object} currentPosition       current calculations for positioning the popup
 * @argument {object} currentPositionType   current type of position or alignment
 *
 * @return {object} result
 */
function _tryRightAlignment(targetRect, childRect, currentPosition, currentPositionType) {
    var available = targetRect.right,
        needed = childRect.width;

    if (needed < available) {
        currentPositionType.alignment = 'right';
        currentPosition.y = targetRect.left - childRect.width + targetRect.width;
    }
}

/**
 * Math to determine if the top alignment is available.
 *
 * @argument {object}   targetRect          position and dimensions of the parent object.
 * @argument {object}   childRect           position and dimensions of the child object.
 * @argument {object}   currentPosition     current calculations for positioning the popup
 * @argument {object}   currentPositionType current type of position or alignment
 * @argument {Boolean}  force               when set on true, forces the positioning (used as fallback)
 *
 * @return {object} result
 */
function _tryTopAlignment(targetRect, childRect, currentPosition, currentPositionType, force) {
    var available = _getPageSize(this.ownerDocument).height - targetRect.top,
        needed = childRect.height;

    if ((needed < available) || force) {
        currentPositionType.alignment = 'top';
        currentPosition.x = targetRect.top;
    }
}

/**
 * Math to determine if the middle alignment is available.
 *
 * @argument {object} targetRect            position and dimensions of the parent object.
 * @argument {object} childRect             position and dimensions of the child object.
 * @argument {object} currentPosition       current calculations for positioning the popup
 * @argument {object} currentPositionType   current type of position or alignment
 *
 * @return {object} result
 */
function _tryMiddleAlignment(targetRect, childRect, currentPosition, currentPositionType) {
    var available = _getPageSize(this.ownerDocument).height,
        child,
        targetPos;

    currentPositionType.alignment = 'middle';

    child = childRect.height;
    targetPos = targetRect.top;

    // Popup top half fits
    if (targetPos + child < available) {
        // Popup fits centered
        currentPosition.x = targetPos;
    } else {
        // Popup doesn't fit on the bottom, so will be sticky to that side
        currentPosition.x = Math.max(0, available - childRect.height);
    }
}

/**
 * Math to determine if the bottom alignment is available.
 *
 * @argument {object} targetRect position and dimensions of the parent object.
 * @argument {object} childRect position and dimensions of the child object.
 * @argument {object} result current calculation for positioning the popup
 *
 * @return {object} result
 */
function _tryBottomAlignment(targetRect, childRect, currentPosition, currentPositionType) {
    var available = targetRect.bottom,
        needed = childRect.height;

    if (needed < available) {
        currentPositionType.alignment = 'bottom';
        currentPosition.x = targetRect.bottom - needed;
    }
}

/**
 * Calculates document width and height relative to viewport (position fixed) in a crossbrowser way
 * @param  {HTMLElement} elementOwnerDocument ownerDocument property of an element contained in the document
 * @return {Object}         object.height   {Number}
 *                          object.width    {Number}
 */
function _getPageSize(elementOwnerDocument) {
    var documentEl = elementOwnerDocument.documentElement;

    return {
        width: Math.max(documentEl.clientWidth, window.innerWidth || 0),
        height: Math.max(documentEl.clientHeight, window.innerHeight || 0)
    };
}

/**
 * Calculates position for the popup
 * @param  {HTMLElement}    component                   component to be rendered as a popup
 * @param  {Object}         target                      contains position and dimensions of target element
 * @param  {Array}          positionOrder               ordered list of possible positions to fit the popup
 * @param  {Object}         alignmentOrder              ordered list of possible alignments to try to fit the popup
 * @param  {Object}         margin                      margin between the popup and the target
 * @param  {Object}         customPositioningMethods    custom position or alignment methods
 * @return {Object}         object.x
 *                          object.y
 *                          object.positionFound
 *                          object.alignmentFound
 */
function _getPopupPosition(component, target, positionOrder, alignmentOrder, margin, customPositioningMethods) {
    var currentPosition = {
            x: -1,
            y: -1
        },
        positionMap = {
            'top': _tryTopPosition,
            'left': _tryLeftPosition,
            'right': _tryRightPosition,
            'bottom': _tryBottomPosition
        },
        alignmentMap = {
            'top': _tryTopAlignment,
            'left': _tryLeftAlignment,
            'right': _tryRightAlignment,
            'bottom': _tryBottomAlignment,
            'middle': _tryMiddleAlignment,
            'center': _tryCenterAlignment
        },
        boxShadow = {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        },
        currentPositionType = {},
        childRect,
        connector = component.querySelector('.connector'),
        targetRect,
        positionFound,
        alignmentFound,
        verticalPosition,
        method,
        posibleAlignments;

    customPositioningMethods = customPositioningMethods || {};
    targetRect = _getRect(target);
    if (!component.style.minWidth) {
        component.style.minWidth = targetRect.width + 'px';
    }
    childRect = _getRect(component);

    // If the component has a connector, we need to add extra margin
    if (connector) {
        margin = BASE_MARGIN + (margin || CONNECTOR_MARGIN);
    } else {
        margin = BASE_MARGIN + (margin || 0);
        boxShadow = getBoxShadowSize(component);
    }

    // Checks every position on the array until one fits on the page
    positionFound = positionOrder.some(function (pos) {
        var method = customPositioningMethods[pos] || positionMap[pos];
        if (method) {
            method.call(target, targetRect, childRect, margin, boxShadow, currentPosition, currentPositionType);
            return currentPositionType.position;
        }
    });

    if (!positionFound) {
        method = customPositioningMethods.bottom || positionMap.bottom;
        // If there's no space anywhere, popup is forced to be added at the bottom
        method.call(target, targetRect, childRect, margin, boxShadow, currentPosition, currentPositionType, true);
    }

    verticalPosition = VERTICAL_POSITIONS.indexOf(currentPositionType.position) > -1;
    if (verticalPosition) {
        posibleAlignments = HORIZONTAL_ALIGNMENTS;
    } else {
        posibleAlignments = VERTICAL_ALIGNMENTS;
    }

    // Check every alignment on the array that matches with the position until one fits on the page
    alignmentFound = alignmentOrder.some(function (align) {
        var method;
        if (posibleAlignments.indexOf(align) > -1) {
            method = customPositioningMethods[align] || alignmentMap[align];
            method.call(target, targetRect, childRect, currentPosition, currentPositionType);
            return currentPositionType.alignment;
        }
    });

    // If there's no space anywhere, popup is forced to be aligned to the top
    if (!alignmentFound && !verticalPosition) {
        method = customPositioningMethods.top || alignmentMap.top;
        alignmentMap.top.call(target, targetRect, childRect, currentPosition, currentPositionType, true);
    }

    if (connector && currentPositionType.alignment === 'middle') {
        // TODO: handle connector with height different from 14px
        // Problem: at this point, '_getRect(connector)' returns 0-values
        connector.style.top = ((Math.min(childRect.height, targetRect.height) - 14) / 2) + 'px';
    }

    return {
        'currentPosition': currentPosition,
        'currentPositionType': currentPositionType
    };
}

/**
 * Resets all position and alignments classes
 * @param  {HTMLElement} component Component to clear classes
 */
function _clearPosition(component) {
    VERTICAL_POSITIONS.forEach(function (pos) {
        this.classList.remove('position-' + pos);
    }, component);

    HORIZONTAL_POSITIONS.forEach(function (pos) {
        this.classList.remove('position-' + pos);
    }, component);

    VERTICAL_ALIGNMENTS.forEach(function (alignment) {
        this.classList.remove('alignment-' + alignment);
    }, component);

    HORIZONTAL_ALIGNMENTS.forEach(function (alignment) {
        this.classList.remove('alignment-' + alignment);
    }, component);
}

/**
 * Updates styles of component with position and alignments
 * @param  {HTMLElement}    component      Component to show as a popup
 * @param  {Object}         positionTarget Position and alignments to be set
 */
function _updateComponent(component, positionTarget) {
    _clearPosition(component);

    component.classList.add('position-' + positionTarget.currentPositionType.position);
    component.classList.add('alignment-' + positionTarget.currentPositionType.alignment);

    component.style.top = positionTarget.currentPosition.x + 'px';
    component.style.left = positionTarget.currentPosition.y + 'px';
}

/**
 * Sets position order and alignment order based on parameters or default values
 * Obtains coordinates for popup
 * Updates component with new coordinates
 * @param  {HTMLElement}    component                   component to show as a popup
 * @param  {HTMLElement}    referenceElement            reference element to position the popup
 * @param  {Array}          positions                   ordered list of possible positions to fit the popup
 * @param  {Array}          alignment                   ordered list of possible alignments to try to fit the popup
 * @param  {Number}         margin                      margin to add to the BASE_MARGIN
 * @param  {Object}         customPositioningMethods    custom position or alignment methods
 * @return {Boolean}                                    true if a position for the popup could be found
 */
function _setPosition(component, referenceElement, positions, alignment, margin, customPositioningMethods) {

    var finalAlignment,
        positionTarget,
        orderPosition;

    orderPosition = positions || ['bottom', 'top'];
    finalAlignment = alignment || ['left', 'right'];

    positionTarget = _getPopupPosition(component, referenceElement, orderPosition, finalAlignment, margin, customPositioningMethods);

    if (!positionTarget.currentPositionType.position || !positionTarget.currentPositionType.alignment) {
        _hide(component);
        return false;
    }

    _updateComponent(component, positionTarget);

    return true;

}

/**
 * Adds listener for browser resize.
 * @param  {HTMLElement}    popupElement                Component where a resize listening method should be installed
 * @param  {HTMLElement}    referenceElement            Reference element to position the popup
 * @param  {Array}          positions                   ordered list of possible positions to fit the popup
 * @param  {Array}          alignment                   ordered list of possible alignments to try to fit the popup
 * @param  {Number}         margin                      margin to add to the BASE_MARGIN
 * @param  {Object}         customPositioningMethods    custom position or alignment methods
 * @return {Function}                                   Return a reference for future removal of listener
 */
function _installResizeMethod(popupElement, referenceElement, positions, alignment, margin, customPositioningMethods) {
    var resizeMethod;

    resizeMethod = function () {
        if (popupElement.classList.contains('visible')) {
            _setPosition(popupElement, referenceElement, positions, alignment, margin, customPositioningMethods);
        }
    };

    Viewport.onResize(resizeMethod);

    return resizeMethod;
}

/**
 * Removes listener for browser resize
 * @param  {Function} callback Reference of the listener to remove
 */
function _uninstallResizeMethod(callback) {
    Viewport.offResize(callback);
}

/**
 * Calculates popup position based on the reference HTML element on conf.target.
 * Then, calls popupElement.show.
 * If extraOpts exists, uses it as an argument for show method
 * @param  {HTMLElement}    popupElement                component to show as a popup
 * @param  {HTMLElement}    referenceElement            reference element to position the popup
 * @param  {Array}          positions                   ordered list of possible positions to fit the popup
 * @param  {Array}          alignment                   ordered list of possible alignments to try to fit the popup
 * @param  {Object}         extraOpts                   Optional. Properties to be send to the popupElement
 * @param  {Number}         margin                      margin to add to the BASE_MARGIN
 * @param  {Object}         customPositioningMethods    custom position or alignment methods
 */
function _show(popupElement, referenceElement, positions, alignment, extraOpts, margin, customPositioningMethods) {
    var setupSuccessful = false;

    if (!document.body.contains(referenceElement)) {
        // Silently fail if the target is not in the DOM.
        return;
    }

    if (popupElement.show && !popupElement.classList.contains('visible')) {
        setupSuccessful = _setPosition(popupElement, referenceElement, positions, alignment, margin, customPositioningMethods);
        if (setupSuccessful) {
            popupElement.show(extraOpts);
        }
    }

}

/**
 * Hides the previously shown component
 * @param  {HTMLElement} popupElement Component to be hidden
 */
function _hide(popupElement) {
    if (popupElement.classList.contains('visible')) {
        popupElement.close();
    }
}

module.exports = {
    installResizeMethod: _installResizeMethod,
    uninstallResizeMethod: _uninstallResizeMethod,
    clearPosition: _clearPosition,
    setPosition: _setPosition,
    show: _show,
    hide: _hide
};



/** WEBPACK FOOTER **
 ** ./src/utils/PopupUtils.js
 **/